import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw2501Api, F2110, QueryRequest, ApproveResponse, UnapproveResponse } from '../../../services/pw2501Api';

export interface QueryFilter {
  dateFrom: string;
  dateTo: string;
  vendorFrom: string;
  vendorTo: string;
}

export interface UsePW2501QueryReturn {
  // State
  loading: boolean;
  approving: boolean;
  pendingItems: F2110[];
  selectedKeys: string[];
  filter: QueryFilter;
  postToInventory: boolean;

  // Actions
  setFilter: (f: QueryFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  setPostToInventory: (v: boolean) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
  batchUnapprove: () => Promise<UnapproveResponse | null>;
}

export function usePW2501Query(): UsePW2501QueryReturn {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingItems, setPendingItems] = useState<F2110[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [postToInventory, setPostToInventory] = useState(true);
  const [filter, setFilter] = useState<QueryFilter>({
    dateFrom: '',
    dateTo: '',
    vendorFrom: '',
    vendorTo: '',
  });

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const req: QueryRequest = {
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        vendorFrom: filter.vendorFrom,
        vendorTo: filter.vendorTo,
      };
      const resp = await pw2501Api.query(req);
      setPendingItems(resp.data || []);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆入庫單進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await pw2501Api.approve(selectedKeys, postToInventory);
      message.success(resp.message);
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, postToInventory, fetchPending]);

  const batchUnapprove = useCallback(async (): Promise<UnapproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆入庫單進行取消核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await pw2501Api.unapprove(selectedKeys);
      message.success(resp.message);
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '取消核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    pendingItems,
    selectedKeys,
    filter,
    postToInventory,
    setFilter,
    setSelectedKeys,
    setPostToInventory,
    fetchPending,
    batchApprove,
    batchUnapprove,
  };
}
